import {Component, attr} from "../Component";
import {element} from "../CustomElement";
import {i18n} from "../../i18n/I18n";
import {icons} from "../../icons/Icons";
import {compareCoupon, isHeroCoupon} from "../../utils/coupons";
import * as array from "../../utils/array";

import {Dialog, DialogHeader} from "../flipp-dialog/Dialog";
import {ToastContainer} from "../flipp-toast-container/ToastContainer";
import {CouponClipping} from "../flipp-coupon-clipping/CouponClipping";
import {LoyaltyCardInputElement} from "../flipp-loyalty-card/LoyaltyCardInputElement";
import {ToastService} from "../../services/ToastService";

import {inject} from "../../services/di";
import {LocationService, Country} from "../../services/LocationService";
import {AnalyticsService} from "../../services/AnalyticsService";
import {AuthenticationService} from "../../services/AuthenticationService";
import {CouponService} from "../../services/CouponService";
import {AccountsService} from "../../services/AccountsService";
import {EventService} from "../../services/EventService";
import {LoyaltyProgramService} from "../../services/LoyaltyProgramService";
import {Coupon, CouponRedemptionStatus} from "../../definitions/CouponDefinitions";
import {LoyaltyCard, LoyaltyProgram, LoyaltyProgramCoupon, ToastLevel} from "../../definitions/CommonDefinitions";
import {LoyaltyCardSource} from "../../services/definitions/IAnalyticsService";

interface LoyaltyCardElementProps extends JSX.HTMLProps {
	cardId: string;
	loyaltyProgramId: number;
}

interface LoyaltyCardModel {
	card: LoyaltyCard;
	program: LoyaltyProgram;
	coupons: Coupon[];
}

@element("flipp-loyalty-card")
export class LoyaltyCardElement extends Component<LoyaltyCardElementProps, LoyaltyCardModel> {

	@inject auth: AuthenticationService;
	@inject loyaltyService: LoyaltyProgramService;
	@inject couponService: CouponService;
	@inject accountsService: AccountsService;
	@inject eventService: EventService;
	@inject location: LocationService;
	@inject analytics: AnalyticsService;
	@inject toast: ToastService;

	@attr cardId: string;
	@attr loyaltyProgramId: number;

	refs: {
		removeDialog: Dialog;
		removeButton: HTMLButtonElement;
	};

	private loyaltyCardDialog: Dialog = null;

	onAttach() {
		window.addEventListener("flipp-couponclipped", this.handleCouponClipped_);
	}

	onDetach() {
		window.removeEventListener("flipp-couponclipped", this.handleCouponClipped_);
	}

	prepare() {
		return Promise.all([
			this.loyaltyService.getLoyaltyCard(this.cardId, this.auth.user),
			this.loyaltyService.getLoyaltyProgram(this.loyaltyProgramId),
			this.couponService.getClippedCoupons(this.auth.user),
			this.couponService.getCoupons()
		]).then(([card, program, clipped, coupons]) => {
			if (!program || !card) {
				return null;
			}
			const clippedIds = clipped.filter((coupon) =>
				coupon.status === CouponRedemptionStatus.CLIPPED &&
				coupon.program.loyaltyProgramId === program.id
			).map((coupon) => coupon.program.couponId);

			const clippedCoupons: Coupon[] = [];
			coupons.forEach((coupon) => {
				if (clippedIds.indexOf(coupon.id) !== -1) {
					clippedCoupons.push(coupon);
				}
			});

			return {card, program, coupons: clippedCoupons};
		});
	}

	renderLoading() {
		return <span></span>;
	}

	render(model: LoyaltyCardModel) {
		if (!model) {
			return null;
		}

		return <div>
				<button
					classList={{"unavailable": !model.card.registered}}
					onClick={this.showLoyaltyCardDialog_}>
					<div flex-vertical>
						<div class="badge" flex-vertical>{i18n("unavailable")}</div>
						<img src={model.program.cardImageUrl} alt="" />
						<span>{model.card.name}</span>
					</div>
				</button>
			</div>;
	}

	private getLoyaltyCardPop = () => {

		if (this.loyaltyCardDialog) {
			return this.loyaltyCardDialog;
		}

		const model = this.model;
		const coupons = model.coupons;
		const heroCoupons: Coupon[] = [];
		const rest: Coupon[] = [];
		coupons.forEach((coupon) => {
			if (isHeroCoupon(coupon)) {
				heroCoupons.push(coupon);
			} else {
				rest.push(coupon);
			}
		});
		heroCoupons.sort(compareCoupon);
		rest.sort(compareCoupon);

		const sortedCoupons = heroCoupons.concat(rest);
		const isCanada = this.location.country === Country.CANADA;

		this.loyaltyCardDialog = <Dialog class="loyalty-card-details" classList={{"canada": isCanada}} transient>
				<DialogHeader>
					<h1 content-slot="title">{model.card.name}</h1>
				</DialogHeader>

				<ToastContainer>
					<div>
						<div class="card-details-container" flex-horizontal>
							<div class="loyalty-card-wrapper" flex-horizontal>
								<div class="details" flex-vertical>
									<img src={model.program.cardImageUrl} alt="" />
									<div class="cardid">
										{model.program.validationMethod === "cardonly" ?
											model.card.cardId
										: model.program.validationMethod === "phoneonly" ||
											model.program.validationMethod === "phoneandpin" ?
											model.card.phoneNumber
										: model.program.validationMethod === "cardorphone" ?
											model.card.cardId || model.card.phoneNumber
										: null
										}
									</div>
									<button
										class="remove-card"
										onClick={this.showRemoveCardDialog_}
										ref="removeButton"
										anchor-point="left bottom">
										{i18n("remove_card")}
									</button>
								</div>

								<div class="coupons">
								{sortedCoupons.length > 0 ?
									sortedCoupons.map((coupon) =>
										<CouponClipping couponId={coupon.id} />)
								:
									<div class="none-clipped" flex-horizontal>{i18n("no_coupons_clipped")}</div>
								}
								</div>
							</div>
						</div>

					</div>
				</ToastContainer>
			</Dialog> as Dialog;

		this.loyaltyCardDialog.addEventListener("flipp-toast", (evt: CustomEvent) => {
			/* Intercept toast events and dispatch them on the parent dialog */
			evt.stopPropagation();
			evt.preventDefault();

			const container = this.loyaltyCardDialog.querySelector("flipp-toast-container");
			if (container) {
				this.toast.fireToastEvent(evt.detail.message, evt.detail.level, evt.detail.closeable, container);
			}
		});


		this.loyaltyCardDialog.addEventListener("flipp-navigate", (evt: CustomEvent) => {
			if (this.loyaltyCardDialog.open) {
				this.loyaltyCardDialog.close();
			}
			this.loyaltyCardDialog = null;
		});

		return this.loyaltyCardDialog;
	}

	private makeRemoveCardPop = () => {
		const dialog = <Dialog popover
				class="remove-card-dialog"
				anchor-point="left top"
				transient>
				<RemoveLoyaltyCardDialog cardId={this.cardId} />
			</Dialog> as Dialog;

		dialog.addEventListener("flipp-loyaltycards", (e: CustomEvent) => {
			if (e.detail && e.detail.action === "remove") {
				const parent = this.getLoyaltyCardPop();
				dialog.close();
				parent.close();
			}
		});

		dialog.addEventListener("flipp-toast", (evt: CustomEvent) => {
			/* Intercept toast events and dispatch them on the parent dialog */
			evt.stopPropagation();
			evt.preventDefault();
			const parent = this.getLoyaltyCardPop();
			this.toast.fireToastEvent(evt.detail.message, evt.detail.level as ToastLevel, evt.detail.closeable, parent);
		});

		return dialog;
	}

	private handleCouponClipped_ = (e: Event) => {
		this.invalidate();
	}

	private showLoyaltyCardDialog_ = (e: Event) => {
		const dialog = this.getLoyaltyCardPop() as Dialog;
		dialog.showModal();
	}

	private showRemoveCardDialog_ = (e: Event) => {
		e.preventDefault();
		const dialog = this.makeRemoveCardPop() as Dialog;
		dialog.showModal(e.target as HTMLButtonElement);
	}
}

interface LoyaltyCardItemTableProps extends JSX.HTMLProps {
	editingProgramId?: number;
}

type LoyaltyCardItemTableModel = [LoyaltyProgram[], LoyaltyProgramCoupon[]];

@element("flipp-loyalty-card-item-table")
export class LoyaltyCardItemTable extends Component<LoyaltyCardItemTableProps, LoyaltyCardItemTableModel> {

	@inject auth: AuthenticationService;
	@inject loyaltyService: LoyaltyProgramService;
	@inject couponService: CouponService;
	@attr editingProgramId: number;

	refs: {
		cardListing: HTMLDivElement,
		cardFilter: HTMLInputElement
	};

	state = {
		scrollTop: 0,
		filterInput: ""
	};

	prepare() {
		return Promise.all([
			this.loyaltyService.getLoyaltyPrograms(),
			this.loyaltyService.getLoyaltyCards(this.auth.user),
			this.couponService.getLoyaltyProgramCoupons()
		]).then(([programs, cards, coupons]) => {
			/* Filter out loyalty programs if the user already has a corresponding
				* card*/
			const currentCards = cards.map((card) => card.loyaltyProgramId);
			const filteredPrograms = programs.filter((program) =>
				currentCards.indexOf(program.id) === -1);
			return [filteredPrograms, coupons] as LoyaltyCardItemTableModel;
		});
	}

	onAttach() {
		this.addEventListener("reset", this.handleInputReset_);
		window.addEventListener("flipp-loyaltycards", this.handleLoyaltyCardEvent_);
		window.addEventListener("close", this._handleDialogCloseEvent);
	}

	onDetach() {
		this.removeEventListener("reset", this.handleInputReset_);
		window.removeEventListener("flipp-loyaltycards", this.handleLoyaltyCardEvent_);
		window.removeEventListener("close", this._handleDialogCloseEvent);
	}

	onRender() {
		this.refs.cardListing.scrollTop = this.state.scrollTop;
		if (typeof this.state.filterInput === "string") {
			this.refs.cardFilter.value = this.state.filterInput;
		}
		if (this.state.filterInput.length > 0) {
			this.refs.cardFilter.setSelectionRange(this.state.filterInput.length, this.state.filterInput.length);
		}
		this.refs.cardFilter.focus();
	}

	render([programs, coupons]: LoyaltyCardItemTableModel) {

		const filteredPrograms = programs.filter((program) =>
			program.name.toLowerCase().indexOf(this.state.filterInput.toLowerCase()) === 0);

		/* Group coupons by loyalty program ID */
		const groupedCoupons = array.groupBy(coupons,
			(coupon) => String(coupon.loyaltyProgramId));

		return <div>
			<form onSubmit={this.handleFilterForm_} class="filter-form" flex-horizontal flex-grow>
				<input
					type="text"
					name="cardfilter"
					ref="cardFilter"
					placeholder={i18n("search_cards")}
					onInput={this.handleFilterCards_}
					flex-grow/>
				<div class="filter-icon" flex-horizontal>{icons.search}</div>
			</form>
			<div class="card-listing" ref="cardListing">
				{filteredPrograms.length === 0 ?
					<div class="no-programs" flex-horizontal>{i18n("no_loyalty_programs")}</div>
				:
					null
				}
				{filteredPrograms.map((program) => {
					const programMatch = array.find(groupedCoupons, (grouping) => {
						return grouping[0] === String(program.id);
					});
					const couponNum = programMatch ? programMatch[1].length : 0;
					return <div onClick={this.handleCardEditing_.bind(this, program.id)} flex-vertical tabIndex={0}>
							<div class="card-details-container" flex-horizontal flex-grow>
								<div class="add" flex-vertical>{icons.plus}</div>
								<img src={program.cardImageUrl} alt="" />
								<div flex-vertical flex-grow>
									<div class="name" flex-horizontal flex-grow>
										<div class="name" flex-grow>{program.name}</div>
										<div class="coupons">
											{couponNum > 1 ?
												i18n("multi_coupon_available", {num: String(couponNum)})
											: couponNum === 1 ?
												i18n("one_coupon_available")
											:
												null
											}
										</div>
									</div>
								</div>
							</div>
							{program.id === this.editingProgramId ?
								<LoyaltyCardInputElement
									programId={program.id}
									editing={program.id === this.editingProgramId}
									cancelable={true}
									context={LoyaltyCardSource.CouponPage} />
							: null }
						</div>;
					}
				)}
			</div>
		</div>;
	}

	private handleInputReset_ = (e: Event) => {
		e.stopPropagation();
		e.preventDefault();
		this.editingProgramId = -1;
	}

	private handleLoyaltyCardEvent_ = (e: CustomEvent) => {
		if (e.detail && e.detail.action === "add") {
			this.editingProgramId = -1;

			// Once a loyalty card is added, make sure to clear the filter input.
			// --------------------------------------------------------------------
			this.state.filterInput = "";
		}
	}

	private _handleDialogCloseEvent = (e: Event) => {
		// If the add card dialog is closed, whether by clicking on X, clicking
		// outside of it OR pressing ESC, make sure to clear the filter input.
		// --------------------------------------------------------------------
		this.state.filterInput = "";
	}

	private handleCardEditing_ = (id: number, e: Event) => {
		this.state.scrollTop = this.refs.cardListing.scrollTop;
		this.editingProgramId = id;
	}

	private handleFilterCards_ = (e: Event) => {
		const target = e.target as HTMLInputElement;
		this.state.filterInput = target.value;
		this.invalidate();
	}

	private handleFilterForm_ = (e: Event) => {
		e.preventDefault();
	}

}

interface AddLoyaltyCardModel {
	programs: LoyaltyProgram[];
}

@element("flipp-add-loyalty-card")
export class AddLoyaltyCardElement extends Component<{}, AddLoyaltyCardModel> {

	@inject auth: AuthenticationService;
	@inject loyaltyService: LoyaltyProgramService;
	@inject couponService: CouponService;

	prepare() {

		return Promise.all([
			this.loyaltyService.getLoyaltyPrograms(),
			this.loyaltyService.getLoyaltyCards(this.auth.user),
			/* We don't actually need to request coupons, but we do it here to ensure the
			 * 'Add loyalty card' button doesn't appear until all cards are loaded */
			this.couponService.getClippedCoupons(this.auth.user)
		]).then(([programs, cards]) => {
			/* Filter out loyalty programs if the user already has a corresponding
				* card*/
			const currentCards = cards.map((card) => card.loyaltyProgramId);
			const filteredPrograms = programs.filter((program) =>
				currentCards.indexOf(program.id) === -1);
			return {
				programs: filteredPrograms
			};
		});
	}

	renderLoading() {
		return <span></span>;
	}

	render(model: AddLoyaltyCardModel) {
		return <div>
			<div class="add-card" flex-vertical onClick={this.showAddCardDialog_}>
				<button>
					<div flex-vertical>
						<img class="icon" src="https://f.wishabi.net/creative/Distribution/images/icons/add.svg" alt=""/>
					</div>
				</button>
				<span>{i18n("add_card")}</span>
			</div>
		</div>;
	}

	private makeLoyaltyCardDialog = () => {
		return <Dialog class="add-card-dialog" transient>
			<DialogHeader>
				<h1 content-slot="title">{i18n("add_card")}</h1>
			</DialogHeader>

			<ToastContainer>
				<LoyaltyCardItemTable/>
			</ToastContainer>
		</Dialog>;
	}

	private showAddCardDialog_ = (e: Event) => {
		const dialog = this.makeLoyaltyCardDialog() as Dialog;
		dialog.showModal();
	}
}

interface RemoveLoyaltyCardDialogProps extends JSX.HTMLProps {
	cardId: string;
}

@element("flipp-remove-loyalty-card-dialog")
export class RemoveLoyaltyCardDialog extends Component<RemoveLoyaltyCardDialogProps, LoyaltyCard> {

	@inject accountsService: AccountsService;
	@inject auth: AuthenticationService;
	@inject loyaltyService: LoyaltyProgramService;
	@inject eventService: EventService;
	@inject couponService: CouponService;
	@inject analytics: AnalyticsService;
	@inject toast: ToastService;

	@attr cardId: string;

	state = {
		removing: false
	};

	get removing() {
		return this.state.removing;
	}

	prepare() {
		return this.loyaltyService.getLoyaltyCard(this.cardId, this.auth.user);
	}

	render() {
		return <div>
			<div class="arrow"></div>
			<div flex-vertical>
				<div>{i18n("remove_card_text")}</div>
				<div class="button-area">
					{this.state.removing ?
						<div class="removing-msg" flex-horizontal>
							<div class="spinner"></div>
							<div class="message">{i18n("removing")}</div>
						</div>
					:
						<div flex-horizontal>
							<button
								class="yes"
								onClick={this.handleRemoveCard_}>{i18n("remove_card_yes")}
							</button>
							<button onClick={this.handleClose_}>{i18n("cancel")}</button>
						</div>
					}
				</div>
			</div>
		</div>;
	}

	private handleRemoveCard_ = (e: Event) => {
		e.preventDefault();
		this.state.removing = true;
		this.invalidate().then(() => {
			this.accountsService.deleteLoyaltyCards(this.auth.user.token, this.model).then((resp) => {
				this.couponService.clearClippedCoupons();
				this.loyaltyService.clearLoyaltyCards();
				this.eventService.fireLoyaltyCardEvent("remove", this.model.loyaltyProgramId, this, true);
				this.state.removing = false;
				this.analytics.trackLoyaltyCardRemoved(this.model.loyaltyProgramId);
			}, (err: Error) => {
				this.toast.fireToastEvent(i18n("remove_card_error"), ToastLevel.ERROR, false, this);
				this.state.removing = false;
				this.eventService.fireDialogCloseEvent(this);
			});

		});
	}

	private handleClose_ = (e: Event) => {
		this.eventService.fireDialogCloseEvent(this);
	}
}
